import * as Ably from 'ably'
import { API_BASE } from './constants.js'

/**
 * Singleton Ably client for the whole app.
 *
 * Uses token auth via our worker's /ably-token endpoint — the root API
 * key never leaves the server. The client handles its own refresh.
 *
 * If the worker returns 503 (no ABLY_API_KEY set), we stay in a
 * "disconnected" state and everything falls back to the 15s polling
 * so the dashboard still works without real-time.
 *
 * Channel name: "ct-q2-goals" (all users share this single channel).
 */

let client = null
let connectPromise = null
let disabled = false

export const MAIN_CHANNEL = 'ct-q2-goals'

/**
 * Get (or lazily create) the Ably client. Resolves even if Ably is not
 * configured on the worker — in that case returns null and the caller
 * should no-op.
 */
export async function getAblyClient() {
  if (disabled) return null
  if (client) return client
  if (connectPromise) return connectPromise

  connectPromise = (async () => {
    try {
      // Probe the token endpoint once — if it 503s, we stay disabled.
      const probe = await fetch(API_BASE + '/ably-token')
      if (probe.status === 503) {
        console.info('[ably] ABLY_API_KEY not configured on worker — real-time disabled, falling back to polling')
        disabled = true
        return null
      }
      if (!probe.ok) {
        console.warn('[ably] /ably-token probe returned HTTP', probe.status)
        disabled = true
        return null
      }

      client = new Ably.Realtime({
        authUrl: API_BASE + '/ably-token',
        // Don't echo our own published messages back to our own subscription.
        // Without this, a publish triggers our own listener which would
        // cause an invalidate → refetch → stale KV read → item resurrection.
        echoMessages: false,
        // Reconnect aggressively — this is a collaboration tool
        disconnectedRetryTimeout: 3000,
        suspendedRetryTimeout: 10000,
      })
      return client
    } catch (err) {
      console.warn('[ably] client init failed', err)
      disabled = true
      return null
    } finally {
      connectPromise = null
    }
  })()

  return connectPromise
}

/**
 * Whether real-time is available (i.e., worker has ABLY_API_KEY and the
 * client connected). Useful for the PresenceBar to show a "live" indicator.
 */
export function isAblyEnabled() {
  return !disabled && !!client
}
